import {defineStore} from 'pinia';
import api from '@/lib/api';
import {getToken, setToken} from '@/lib/token';
import {getUserInfo, setUserInfo, type StoredUserInfo} from '@/lib/user';
import {useOrganizationStore} from '@/stores/organization';
import {useSubscriptionStore} from '@/stores/subscription';
import {useToastStore} from '@/stores/toast';
import {RESEND_COOLDOWN_SECONDS, retryAfterSeconds} from '@/lib/email-verification';

// Bumped whenever the verification state is reset for another session or account. Verification
// requests started before write no state and resolve to null, as in the subscription store.
let accountGeneration = 0;

// Organization selection and subscription belong to the previous account.
function clearAccountState() {
  useOrganizationStore().clear();
  useSubscriptionStore().invalidate();
}

export interface AuthRequest {
  username: string;
  password: string;
}

export interface RegisterRequest {
  username: string;
  password: string;
  givenName: string;
  familyName: string;
}

export interface UserMeResponse {
  id: number;
  username: string;
  givenName: string | null;
  familyName: string | null;
  emailVerified: boolean;
}

export interface ResetPasswordResponse {
  status: 'ok';
  mfaReenrolled: boolean;
  otpAuthUrl?: string;
}

export type VerifyEmailResult =
  | { status: 'ok' | 'already_verified' }
  | { status: 'invalid_code'; attemptsRemaining: number }
  | { status: 'code_expired' };

export type ResendResult =
  | { status: 'sent' }
  | { status: 'already_verified' }
  | { status: 'rate_limited'; retryAfterSeconds: number };

export interface AuthResponse {
  username: string;
  token: string;
  mfaEnabled: boolean;
  givenName?: string | null;
  familyName?: string | null;
}

// Lock details arrive at the top level or nested under `properties`.
interface AccountLockBody {
  lockedUntil?: string;
  lockRemainingSeconds?: number;
  properties?: AccountLockBody;
}

interface ErrorBody extends AccountLockBody {
  message?: string;
}

// Axios errors, or any rejection carrying the same fields.
interface RequestError {
  response?: { status: number; data?: ErrorBody };
  request?: unknown;
  message?: string;
}

// The unlock timer handle lives on the store instance but outside its reactive state.
interface LockTimerHolder {
  lockUntilMs: number | null;
  _lockTimerId?: ReturnType<typeof setTimeout> | null;
}

interface State {
  token: string | null;
  loading: boolean;
  error: string | null;
  username: string | null;
  givenName: string | null;
  familyName: string | null;
  mfaRequired: boolean;
  otpAuthUrl: string | null;
  lockUntilMs: number | null;
  emailVerified: boolean | null;
  resendAvailableAtMs: number | null;
}

export const useAuthStore = defineStore('auth', {
  state: (): State => {
    const user = getUserInfo();
    return {
      token: getToken(),
      loading: false,
      error: null,
      username: user.username,
      givenName: user.givenName,
      familyName: user.familyName,
      mfaRequired: false,
      otpAuthUrl: null,
      lockUntilMs: null,
      emailVerified: null,
      resendAvailableAtMs: null,
    };
  },
  getters: {
    isAuthenticated: (state) => Boolean(state.token) && !state.mfaRequired,
    isLoginLocked(state): boolean {
      // Keep the auto-unlock timer aligned with the current timer implementation (real or mocked)
      // Calling an internal action here re-schedules the timeout if needed so tests using fake timers work reliably.
      if (state.lockUntilMs && state.lockUntilMs > Date.now()) {
        // Getters are not typed with actions, although Pinia exposes them on `this`.
        (this as unknown as { _ensureUnlockTimer(atMs: number): void })._ensureUnlockTimer(
          state.lockUntilMs
        );
      }
      return state.lockUntilMs !== null && Date.now() < state.lockUntilMs;
    },
    fullName: (state): string | null => {
      const parts = [state.givenName, state.familyName].filter(Boolean);
      return parts.length ? (parts as string[]).join(' ') : null;
    },
    displayName(): string {
      // Prefer full name, then username, then generic fallback
      // Use `this` to access other getters/state in Pinia
      return this.fullName || this.username || 'User';
    },
  },
  actions: {
    _ensureUnlockTimer(atMs: number) {
      // Always reschedule to bind to the current timer implementation (real or mocked)
      this._scheduleUnlock(atMs);
    },
    _clearUnlockTimer() {
      const self = this as LockTimerHolder;
      if (self._lockTimerId) {
        clearTimeout(self._lockTimerId);
        self._lockTimerId = null;
      }
    },
    _scheduleUnlock(atMs: number) {
      this._clearUnlockTimer();
      const delay = Math.max(0, atMs - Date.now());
      const self = this as LockTimerHolder;
      self._lockTimerId = setTimeout(() => {
        self.lockUntilMs = null;
        self._lockTimerId = null;
      }, delay);
    },
    _applyAccountLock(data: AccountLockBody) {
      const props = data && data.properties ? data.properties : data;
      let untilMs: number | null = null;
      if (props && props.lockedUntil) {
        const parsed = Date.parse(props.lockedUntil);
        if (!Number.isNaN(parsed)) {
          untilMs = parsed;
        }
      }
      // Fallback to lockRemainingSeconds if lockedUntil is not provided or invalid
      if (
        untilMs === null &&
        typeof props?.lockRemainingSeconds === 'number' &&
        isFinite(props.lockRemainingSeconds)
      ) {
        const remaining = Math.max(0, Math.round(props.lockRemainingSeconds));
        untilMs = Date.now() + remaining * 1000;
      }
      if (untilMs && untilMs > Date.now()) {
        this.lockUntilMs = untilMs;
        this._scheduleUnlock(untilMs);
      }
    },
    _setError(error: unknown, contextMessage: string) {
      const e = error as RequestError;
      if (e.response) {
        const status = e.response.status;
        const data = e.response.data || {};

        // 1. Always prefer the server's specific message when present
        if (data?.message) {
          this.error = data.message;
        }
        // 2. Explicit handling for account lock (HTTP 423)
        else if (status === 423) {
          let msg = 'Your account is temporarily locked due to failed login attempts.';
          let remaining: number | null = null;
          const props = data && data.properties ? data.properties : data;
          if (
            typeof props?.lockRemainingSeconds === 'number' &&
            isFinite(props.lockRemainingSeconds)
          ) {
            remaining = Math.max(0, Math.round(props.lockRemainingSeconds));
          }
          if (remaining !== null && Number.isFinite(remaining) && remaining > 0) {
            const mins = Math.floor(remaining / 60);
            const secs = remaining % 60;
            const secsPart = secs ? `${secs} second${secs === 1 ? '' : 's'}` : '';
            const timeStr =
              mins > 0
                ? `${mins} minute${mins === 1 ? '' : 's'}${secsPart ? ' ' + secsPart : ''}`
                : `${secs} second${secs === 1 ? '' : 's'}`;
            msg += ` Please try again in ${timeStr}.`;
          } else {
            msg += ' Please try again later.';
          }
          this.error = msg;
          // apply lock timing for UI disable/auto-release
          this._applyAccountLock(data);
        }
        // 3. Use the context-specific message for 401/403
        else if (status === 401 || status === 403) {
          this.error = contextMessage;
        }
        // 4. Fallback for other server errors
        else {
          this.error = `A server error occurred (Status ${status}).`;
        }
      } else if (e.request) {
        // 5. Network error
        this.error = 'Unable to connect to the server. Please check your network.';
      } else {
        // 6. Other JS errors
        this.error = e.message || 'An unknown error occurred.';
      }
    },
    async register(payload: RegisterRequest) {
      this.loading = true;
      this.error = null;
      // Clear any previous OTP url before new registration attempt
      this.otpAuthUrl = null;
      try {
        const { data } = await api.post<{ otpAuthUrl: string }>('auth/register', payload);
        this.otpAuthUrl = data?.otpAuthUrl ?? null;
        return this.otpAuthUrl;
      } catch (e) {
        this._setError(e, 'Registration failed. Please try again.');
        throw e;
      } finally {
        this.loading = false;
      }
    },
    async login(payload: AuthRequest) {
      this.loading = true;
      this.error = null;
      try {
        const { data } = await api.post<AuthResponse>('auth/token', payload);
        clearAccountState();
        this._resetVerification();
        // Successful login clears any prior lock state
        this.lockUntilMs = null;
        this._clearUnlockTimer();
        this.token = data.token;
        this.username = data.username ?? null;
        this.givenName = data.givenName ?? null;
        this.familyName = data.familyName ?? null;
        // If MFA is enabled, require second factor before considering authenticated
        this.mfaRequired = data.mfaEnabled;
        setToken(data.token, !data.mfaEnabled);
        const userInfo: StoredUserInfo = {
          username: this.username,
          givenName: this.givenName,
          familyName: this.familyName,
        };
        setUserInfo(userInfo);
      } catch (e) {
        // --- Updated Error Handling ---
        this._setError(e, 'Invalid username or password. Please try again.');
        this._resetVerification();
        this.token = null;
        this.username = null;
        this.givenName = null;
        this.familyName = null;
        this.mfaRequired = false;
        setToken(null);
        setUserInfo(null);
        throw e;
      } finally {
        this.loading = false;
      }
    },
    async verifyMfa(code: string) {
      this.loading = true;
      this.error = null;
      try {
        const { data } = await api.post<{ status: string }>('auth/mfa', { code });
        if (data?.status && data.status.toLowerCase() === 'ok') {
          this.mfaRequired = false;
          setToken(this.token);
        } else {
          throw new Error('Invalid verification status');
        }
      } catch (e) {
        this._setError(e, 'Invalid mfa. Please try again.');
        throw e;
      } finally {
        this.loading = false;
      }
    },
    cancelMfa() {
      this._resetVerification();
      // Allow user to abort and try logging in again
      this.mfaRequired = false;
      this.token = null;
      this.username = null;
      this.givenName = null;
      this.familyName = null;
      // Clear any login lock state when cancelling MFA (user will attempt fresh login)
      this.lockUntilMs = null;
      this._clearUnlockTimer();
      setToken(null);
      setUserInfo(null);
    },
    logout() {
      this._resetVerification();
      this.token = null;
      this.username = null;
      this.givenName = null;
      this.familyName = null;
      this.mfaRequired = false;
      // Clear any login lock state when logging out
      this.lockUntilMs = null;
      this._clearUnlockTimer();
      setToken(null);
      setUserInfo(null);
      clearAccountState();
    },
    async changePassword(payload: { currentPassword: string; newPassword: string }) {
      this.loading = true;
      this.error = null;
      try {
        // Uses axios instance that injects Authorization: Bearer <token>
        await api.post('auth/change-password', payload);
      } catch (e) {
        this._setError(e, 'Invalid password. Please try again.');
        throw e;
      } finally {
        this.loading = false;
      }
    },
    async forgotPassword(email: string) {
      this.loading = true;
      this.error = null;
      try {
        await api.post('auth/forgot-password', { email });
      } catch (e) {
        this._setError(e, 'Unable to request a password reset. Please try again.');
        throw e;
      } finally {
        this.loading = false;
      }
    },
    async resetPassword(payload: { token: string; newPassword: string }) {
      this.loading = true;
      this.error = null;
      try {
        const { data } = await api.post<ResetPasswordResponse>('auth/reset-password', payload);
        return data;
      } catch (e) {
        this._setError(e, 'Unable to reset password. Please try again.');
        throw e;
      } finally {
        this.loading = false;
      }
    },
    /** The current account's generation, to pass to markEmailNotVerified after a request. */
    accountContext(): number {
      return accountGeneration;
    },
    /** Applies an EMAIL_NOT_VERIFIED answer; false when it belongs to a previous account. */
    markEmailNotVerified(context: number): boolean {
      if (context !== accountGeneration) return false;
      this.emailVerified = false;
      return true;
    },
    _resetVerification() {
      accountGeneration++;
      this.emailVerified = null;
      this.resendAvailableAtMs = null;
    },
    async fetchMe() {
      const context = accountGeneration;
      try {
        const { data } = await api.get<UserMeResponse>('user/me');
        if (context !== accountGeneration) return null;
        this.emailVerified = data.emailVerified;
        return data;
      } catch (e) {
        if (context !== accountGeneration) return null;
        throw e;
      }
    },
    /**
     * Completes a verify or a resend that found the address already verified; false when the
     * account changed meanwhile. It runs here rather than in a component because marking the
     * account verified can unmount the component that asked.
     */
    async _completeVerification(context: number) {
      try {
        await this.fetchMe();
      } catch {
        // The verify or resend response already proves the address is verified.
      }
      if (context !== accountGeneration) return false;
      this.emailVerified = true;
      useToastStore().success('Email verified.');
      try {
        // Verification can link a plan bought on the website.
        await useSubscriptionStore().fetchSubscription();
      } catch {
        // Shown through the subscription store's error state.
      }
      return context === accountGeneration;
    },
    async verifyEmail(code: string): Promise<VerifyEmailResult | null> {
      const context = accountGeneration;
      try {
        const { data } = await api.post<{ status?: string }>('auth/verify-email', { code });
        if (context !== accountGeneration) return null;
        const status = data?.status === 'already_verified' ? 'already_verified' : 'ok';
        return (await this._completeVerification(context)) ? { status } : null;
      } catch (e: unknown) {
        if (context !== accountGeneration) return null;
        const response = (e as { response?: { status?: number; data?: unknown } }).response;
        const body = (response?.data ?? {}) as { status?: string; attemptsRemaining?: unknown };
        if (response?.status === 400 && body.status === 'INVALID_CODE') {
          const attemptsRemaining = Number(body.attemptsRemaining);
          // A code without attempts left is unusable, whatever status accompanied it.
          if (Number.isFinite(attemptsRemaining) && attemptsRemaining > 0) {
            return { status: 'invalid_code', attemptsRemaining };
          }
          return { status: 'code_expired' };
        }
        if (response?.status === 400 && body.status === 'CODE_EXPIRED') {
          return { status: 'code_expired' };
        }
        throw e;
      }
    },
    async resendVerification(): Promise<ResendResult | null> {
      const context = accountGeneration;
      try {
        const { data } = await api.post<{ status?: string }>('auth/verify-email/resend');
        if (context !== accountGeneration) return null;
        if (data?.status === 'already_verified') {
          return (await this._completeVerification(context)) ? { status: 'already_verified' } : null;
        }
        this.resendAvailableAtMs = Date.now() + RESEND_COOLDOWN_SECONDS * 1000;
        return { status: 'sent' };
      } catch (e: unknown) {
        if (context !== accountGeneration) return null;
        if ((e as { response?: { status?: number } }).response?.status === 429) {
          const seconds = retryAfterSeconds(e) ?? RESEND_COOLDOWN_SECONDS;
          this.resendAvailableAtMs = Date.now() + seconds * 1000;
          return { status: 'rate_limited', retryAfterSeconds: seconds };
        }
        throw e;
      }
    },
  },
});
